import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import { isFreePlan } from '../../utils/planAccess';
import { toAssetUrl } from '../../utils/assetBase';
import UpgradeModal from '../../components/UpgradeModal';
import { confirm } from '../../components/ConfirmDialog';
import { fmtTime, fmtDateTime } from '../../utils/dateFormat';
import { printBill, printGift, getReceiptPrinterName, canFiscalRelay, fiscalForward } from '../../utils/printService';
import { tapxCardCharge } from '../../utils/tapxCharge';
import { isOffline as netIsOffline } from '../../utils/netStatus';
import { offlineEnabled, graceValidToday } from '../../utils/offlineCache';

const WEBVIEW_PRINT_TIMEOUT_MS = 15000;

const getReceiptTitle = (currentUser) => (
    currentUser?.tenant_plan === 'pro'
        ? (currentUser?.tenant_name || 'Fiscal POS')
        : 'Fiscal POS'
);

// Xüsusi logo yoxdursa FISCAL default logosu istifadə edilir (pulsuz plan xaric)
const DEFAULT_RECEIPT_LOGO = `${window.location.origin}/FiscalPOS_black.png`;

const getReceiptLogoUrl = (currentUser) => {
    if (isFreePlan(currentUser?.tenant_plan)) return null;
    if (currentUser?.receipt_logo_url) return toAssetUrl(currentUser.receipt_logo_url);
    return DEFAULT_RECEIPT_LOGO;
};

const trimQty = (qty, digits = 3) => Number(qty || 0).toFixed(digits).replace(/\.?0+$/, '');
const toMoney = (value) => Number(value || 0);
const formatMoney = (value) => `${toMoney(value).toFixed(2)} ₼`;
const clampMoney = (value) => Math.max(0, toMoney(value));

const formatQty = (qty, saleType) => {
    if ((saleType || 'piece') === 'piece') return `${Math.round(Number(qty || 0))}əd`;
    if (saleType === 'weight') return `${trimQty(qty)}kq`;
    if (saleType === 'liter') return `${trimQty(qty)}lt`;
    return trimQty(qty);
};

// Saatlıq məhsul üçün keçən vaxt: 9000 san → "2 saat 30 dəq"
const fmtElapsed = (sec) => {
    const s = Math.max(0, Math.floor(Number(sec) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h} saat ${m} dəq` : `${m} dəq`;
};
// Çek qty sütunu dardır — kompakt forma: "2s30d"
const fmtElapsedShort = (sec) => {
    const s = Math.max(0, Math.floor(Number(sec) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}s${m}d` : `${m}d`;
};

const buildReceiptText = (currentOrder, currentUser) => {
    const title = getReceiptTitle(currentUser);
    const printedAt = fmtDateTime(new Date());

    const subtotal = toMoney(currentOrder?.subtotal);
    const servicePercent = toMoney(currentOrder?.service_percent);
    const serviceAmount = toMoney(currentOrder?.service_amount);
    const discountAmount = toMoney(currentOrder?.discount_amount);
    const appliedDeposit = toMoney(currentOrder?.applied_deposit);
    const grandTotal = toMoney(currentOrder?.grand_total || subtotal + serviceAmount - discountAmount + appliedDeposit);

    const lines = [
        title,
        'Musteri ceki',
        '-------------------------------',
        `Telefon: ${currentOrder?.tenant_phone || '-'}`,
        `Masa: ${currentOrder?.table_name || '-'}`,
        `Sifaris: #${currentOrder?.id || '-'}`,
        `${(currentOrder?.waiter_title || '').trim() || 'Ofisiant'}: ${currentOrder?.waiter_name || '-'}`,
        `Tarix: ${printedAt}`,
        '-------------------------------'
    ];

    // Banket komponentlərini gizlə — çekdə yalnız başlıq (qiymətli) sətir görünür
    (currentOrder?.items || []).filter((it) => !Number(it.is_banquet_component)).forEach((item) => {
        const itemTotal = parseFloat(item.unit_price) * Number(item.qty);
        lines.push(item.item_name);
        lines.push(`  ${formatQty(item.qty, item.sale_type)} x ${parseFloat(item.unit_price).toFixed(2)} = ${itemTotal.toFixed(2)} AZN`);
        if (item.note) lines.push(`  Qeyd: ${item.note}`);
    });

    lines.push('-------------------------------');
    lines.push(`Cem: ${subtotal.toFixed(2)} AZN`);
    lines.push(`${trimQty(servicePercent, 2)}% Servis Haqqi: ${serviceAmount.toFixed(2)} AZN`);
    lines.push(`Depozit: ${appliedDeposit.toFixed(2)} AZN`);
    lines.push(`Endirim: ${discountAmount.toFixed(2)} AZN`);
    lines.push(`Umumi cem: ${grandTotal.toFixed(2)} AZN`);

    if (currentOrder?.payment) {
        const payment = currentOrder.payment;
        const cashAmount = toMoney(payment.cash_amount);
        const cardAmount = toMoney(payment.card_amount);
        lines.push('-------------------------------');
        if (payment.method === 'hesab') {
            lines.push(`Nagd: ${cashAmount.toFixed(2)} AZN`);
            lines.push(`Kart: ${cardAmount.toFixed(2)} AZN`);
        } else {
            lines.push(`Odenis novü: ${payment.method === 'kart' ? 'Kart' : payment.method === 'delivery' ? 'Food delivery' : 'Nagd'}`);
            lines.push(`Odenilib: ${toMoney(payment.amount).toFixed(2)} AZN`);
        }
    }

    if (currentOrder?.note) {
        lines.push('-------------------------------');
        lines.push(`Umumi qeyd: ${currentOrder.note}`);
    }

    lines.push('');
    lines.push('Tesekkur edirik!');
    lines.push('Powered by www.fiscalbusiness.net');

    return lines.join('\n');
};

const hasWebViewBridge = () => Boolean(window.chrome?.webview?.postMessage);

const sendWebViewPrintRequest = (message) => new Promise((resolve, reject) => {
    if (!hasWebViewBridge()) {
        reject(new Error('Bu funksiya yalnız Windows tətbiqində işləyir.'));
        return;
    }

    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const timeoutId = window.setTimeout(() => {
        window.chrome.webview.removeEventListener('message', handleMessage);
        reject(new Error('Çap cavabı gözlənilən vaxtda gəlmədi.'));
    }, WEBVIEW_PRINT_TIMEOUT_MS);

    const handleMessage = (event) => {
        const payload = event?.data;
        if (!payload || payload.type !== 'print-bill-result' || payload.requestId !== requestId) return;

        window.clearTimeout(timeoutId);
        window.chrome.webview.removeEventListener('message', handleMessage);

        if (payload.ok) {
            resolve(payload);
            return;
        }

        reject(new Error(payload.error || 'Çap baş tutmadı.'));
    };

    window.chrome.webview.addEventListener('message', handleMessage);
    window.chrome.webview.postMessage({ ...message, requestId });
});

const WaiterBill = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuthStore();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    // Offline rejim: bu ekranda ödəniş serverə gedir (işləmir) → sifariş ekranına (offline «Hesabı bağla») keç.
    // Ödəniş prosesi gedirsə (terminal/fiskal) toxunma.
    const payingRef = useRef(false);
    payingRef.current = paying;
    useEffect(() => {
        const check = () => {
            if (!netIsOffline() || !offlineEnabled() || !graceValidToday() || payingRef.current) return;
            toast('İnternet yoxdur. Hesabı offline bağlamaq üçün sifariş ekranı açıldı.', { duration: 4000 });
            navigate(`/waiter/order/${orderId}`, { replace: true });
        };
        check();
        window.addEventListener('net-status', check);
        return () => window.removeEventListener('net-status', check);
    }, [orderId, navigate]);
    // POS kart ödənişi prosesi popup-u (relay ilə): { open, message, closable, error }
    const [posPay, setPosPay] = useState({ open: false, message: '', closable: false, error: false });
    const [printing, setPrinting] = useState(false);
    const [savingCharges, setSavingCharges] = useState(false);
    const [method, setMethod] = useState('nagd');
    const [amount, setAmount] = useState('');
    const [cashAmount, setCashAmount] = useState('');
    const [cardAmount, setCardAmount] = useState('');
    const [discountAmount, setDiscountAmount] = useState('0');
    const [depositAmount, setDepositAmount] = useState('0');
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [splitDriver, setSplitDriver] = useState('cash');
    const [serviceExempt, setServiceExempt] = useState(false);
    const [depositWaived, setDepositWaived] = useState(false);
    const cashInputRef = useRef(null);
    const cardInputRef = useRef(null);

    // PIN ilə girən admin/kassir backend-də 'waiter' rolu alır amma is_manager=true olur
    const isManager      = user?.is_manager === true;
    const canPay         = ['admin', 'superadmin', 'kassir'].includes(user?.role) || isManager;
    // Manual (fiskal kassaya getmədən) bağlama YALNIZ admin: parol admin və ya PIN admin (display_role)
    const isAdminUser    = ['admin', 'superadmin'].includes(user?.role) || String(user?.display_role || '').toLowerCase() === 'admin';
    const isKassir       = user?.role === 'kassir';
    const isWaiterOnly   = user?.role === 'waiter' && !isManager;
    const isFree         = isFreePlan(user?.tenant_plan);
    const [posTerminal, setPosTerminal] = useState(false); // kassa POS terminala qoşuludur?
    const [foodDelivery, setFoodDelivery] = useState(false); // «Food delivery» ödənişi bu istifadəçiyə açıqdır? (PRO + superadmin tick + rol)
    const [fdTenant, setFdTenant] = useState(false);           // funksiya tenantda aktivdir
    const [deliveryTableIds, setDeliveryTableIds] = useState([]); // «Yalnız Food delivery» otaqlarının masaları
    // Admin endirimi/servis-0% bloku YALNIZ əsl admin üçün (backend verifyAdmin ilə eyni).
    // administrator/kassir is_manager olsa da görməsin — backend onsuz da 403 qaytarır.
    const canEditCharges = ['admin', 'superadmin'].includes(user?.role);
    const canUseDeposit = user?.tenant_plan === 'pro';

    const loadOrder = useCallback(async () => {
        try {
            const res = await api.get(`/orders/${orderId}`);
            const currentOrder = res.data.data;
            setOrder(currentOrder);
            setDiscountAmount(toMoney(currentOrder.discount_amount).toFixed(2));
            setDepositAmount(toMoney(currentOrder.table_deposit_amount || currentOrder.deposit_amount).toFixed(2));
            setServiceExempt(Boolean(Number(currentOrder.service_exempt || 0)));
            setDepositWaived(Boolean(Number(currentOrder.deposit_waived || 0)));

            const grandTotal = toMoney(currentOrder.grand_total);
            setAmount(grandTotal.toFixed(2));

            const payment = currentOrder.payment;
            if (payment?.method === 'hesab') {
                setMethod('hesab');
                setCashAmount(toMoney(payment.cash_amount).toFixed(2));
                setCardAmount(toMoney(payment.card_amount).toFixed(2));
                setSplitDriver('cash');
            } else if (payment?.method === 'kart') {
                setMethod('kart');
                setCashAmount('0.00');
                setCardAmount(grandTotal.toFixed(2));
            } else if (payment?.method === 'delivery') {
                setMethod('delivery');
                setCashAmount('0.00');
                setCardAmount('0.00');
            } else {
                setMethod('nagd');
                setCashAmount(grandTotal.toFixed(2));
                setCardAmount('0.00');
            }
        } catch {
            toast.error('Sifariş tapılmadı.');
            navigate('/waiter/tables');
        } finally {
            setLoading(false);
        }
    }, [navigate, orderId]);

    useEffect(() => {
        loadOrder();
    }, [loadOrder]);

    // Online qayıdanda (sinxrondan sonra) hesabı serverdən təzələ
    useEffect(() => {
        const onResync = (e) => { if (e?.detail?.reason === 'online') loadOrder(); };
        window.addEventListener('app-resync', onResync);
        return () => window.removeEventListener('app-resync', onResync);
    }, [loadOrder]);

    useEffect(() => {
        if (method !== 'hesab') return;

        const targetInput = splitDriver === 'card' ? cardInputRef.current : cashInputRef.current;
        if (!targetInput) return;

        window.requestAnimationFrame(() => {
            targetInput.focus();
            targetInput.select();
        });
    }, [method, splitDriver]);

    const subtotal = order?.items?.reduce(
        (sum, item) => sum + parseFloat(item.unit_price) * Number(item.qty),
        0
    ) || 0;

    // Set qruplaması — eyni set_group_id-li komponentləri tək sətr kimi göstər
    const displayItems = useMemo(() => {
        const items = order?.items || [];
        const groups = new Map();
        const list = [];
        for (const it of items) {
            if (it.set_group_id) {
                let g = groups.get(it.set_group_id);
                if (!g) {
                    g = {
                        _isSetGroup: true,
                        id: `setgroup_${it.set_group_id}`,
                        item_name: it.note || 'Set',
                        note: '',
                        sale_type: 'piece',
                        _total: 0,
                        _minCount: Infinity
                    };
                    groups.set(it.set_group_id, g);
                    list.push(g);
                }
                g._total += Number(it.qty) * Number(it.unit_price);
                const unit = Number(it.set_unit_qty || 0);
                const cnt = unit > 0 ? Number(it.qty) / unit : 1;
                if (cnt < g._minCount) g._minCount = cnt;
            } else {
                list.push(it);
            }
        }
        for (const g of list) {
            if (g._isSetGroup) {
                g.qty = Math.max(1, Math.round(g._minCount || 1));
                g.unit_price = g.qty > 0 ? g._total / g.qty : 0;
            }
        }
        return list;
    }, [order?.items]);
    const isTakeaway = Number(order?.is_takeaway || 0) === 1;
    const servicePercent = toMoney(order?.service_percent);
    const serviceAmountValue = toMoney(order?.service_amount);
    const discountAmountValue = toMoney(order?.discount_amount);
    const depositTargetValue = toMoney(order?.deposit_amount);
    const appliedDepositValue = toMoney(order?.applied_deposit);
    const grandTotal = toMoney(order?.grand_total || subtotal + serviceAmountValue - discountAmountValue + appliedDepositValue);
    const discountBase = Math.max(0, subtotal + serviceAmountValue);
    const mixedTotal = toMoney(cashAmount) + toMoney(cardAmount);
    const paidAmount = method === 'hesab'
        ? mixedTotal
        : (method === 'kart' || method === 'delivery')
            ? grandTotal
            : toMoney(amount);
    const change = method === 'nagd' ? paidAmount - grandTotal : 0;

    const handleMethodChange = (nextMethod) => {
        setMethod(nextMethod);
        if (nextMethod === 'nagd') {
            setAmount(grandTotal.toFixed(2));
            setCashAmount(grandTotal.toFixed(2));
            setCardAmount('0.00');
            return;
        }

        if (nextMethod === 'kart') {
            setAmount(grandTotal.toFixed(2));
            setCashAmount('0.00');
            setCardAmount(grandTotal.toFixed(2));
            return;
        }

        // Food delivery: pul platformadan gəlir → nağd/kart yoxdur (kassa hesabatında ayrıca)
        if (nextMethod === 'delivery') {
            setAmount(grandTotal.toFixed(2));
            setCashAmount('0.00');
            setCardAmount('0.00');
            return;
        }

        setSplitDriver('cash');
        setAmount(grandTotal.toFixed(2));
        setCashAmount('0.00');
        setCardAmount(grandTotal.toFixed(2));
    };

    const deliveryOnly = fdTenant && order?.table_id != null && deliveryTableIds.includes(Number(order.table_id));
    useEffect(() => {
        if (deliveryOnly && method !== 'delivery') handleMethodChange('delivery');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryOnly, method]);

    const handleCashAmountChange = (nextValue) => {
        setSplitDriver('cash');
        setCashAmount(nextValue);

        const cashValue = clampMoney(nextValue);
        const remaining = Math.max(0, grandTotal - cashValue);
        setCardAmount(remaining.toFixed(2));
    };

    const handleCardAmountChange = (nextValue) => {
        setSplitDriver('card');
        setCardAmount(nextValue);

        const cardValue = clampMoney(nextValue);
        const remaining = Math.max(0, grandTotal - cardValue);
        setCashAmount(remaining.toFixed(2));
    };

    const activateSplitField = (field) => {
        setSplitDriver(field);
    };

    const applyDiscountPreset = (percent) => {
        const p = Number(percent || 0);
        if (p >= 100) {
            // 100% → məhsulları tam endirim et (yalnız subtotal) + servis haqqını da söndür (tick).
            // Beləcə cəm 0 olur. İstifadəçi tick-i götürsə servis (məs. 1.19) qayıdır → cəm 1.19.
            setServiceExempt(true);
            setDiscountAmount(subtotal.toFixed(2));
        } else {
            setDiscountAmount((discountBase * (p / 100)).toFixed(2));
        }
    };

    // Servis tick-i dəyişəndə: söndürüləndə (tick götürüləndə) endirim məhsul cəmindən (subtotal)
    // çox olmasın — yoxsa servis də endirimlə silinər. Beləcə servis ödənilə bilən qalır.
    const handleServiceExemptChange = (checked) => {
        setServiceExempt(checked);
        if (!checked && toMoney(discountAmount) > subtotal + 0.005) {
            setDiscountAmount(subtotal.toFixed(2));
        }
    };

    // Kassa POS terminala qoşuludurmu — ofisiant "POS-a göndər" düyməsi üçün
    useEffect(() => {
        let alive = true;
        api.get('/orders/pos-terminal')
            .then((r) => {
                if (!alive) return;
                setPosTerminal(!!r.data?.pos_terminal);
                setFoodDelivery(!!r.data?.food_delivery);
                setFdTenant(!!r.data?.food_delivery_tenant);
                setDeliveryTableIds(Array.isArray(r.data?.delivery_table_ids) ? r.data.delivery_table_ids.map(Number) : []);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    // Ödənişi icra et — fiskal kassa lokal şəbəkədədirsə C# relay ilə (POS terminala göndər,
    // kart NFC ilə çəkilir), uğurlusa server hesabı bağlayır. Əks halda düz /pay.
    const doPayRequest = async (payParams, forced = false, manual = false) => {
        const params = forced ? { ...payParams, force_sayim: true } : { ...payParams };
        // Admin MANUAL bağlama: fiskal kassaya getmə, birbaşa qeyd et (kart popup-u yox)
        if (manual) {
            try {
                return await api.post(`/orders/${orderId}/pay`, { ...params, skip_fiscal: true });
            } catch (err) {
                if (!forced && err.response?.data?.code === 'SAYIM_CONFIRM') {
                    if (await confirm(err.response?.data?.msg || 'Sayım bağlanmayıb. Yenə də təsdiq edək?')) {
                        return doPayRequest(payParams, true, true);
                    }
                    throw new Error('__cancelled__');
                }
                throw err;
            }
        }
        // Kart çəkiləcəkmi? (POS terminal popup-u yalnız kart hissəsi varsa göstərilir)
        const willCharge = params.method === 'kart' || Number(params.card_amount) > 0;
        const useRelay = canFiscalRelay();
        const amt = params.method === 'hesab' ? Number(params.card_amount) : Number(params.amount || 0);
        const amtLabel = `${(Number(amt) || 0).toFixed(2)} ₼`;
        try {
            if (useRelay) {
                if (willCharge) setPosPay({ open: true, closable: false, error: false, message: `${amtLabel} POS terminala göndərilir...` });
                let prep = await api.post(`/orders/${orderId}/pay-prepare`, params);

                // Caspos + TapXPhone: əvvəl kartı terminalda çək (aç→yoxla→rrn), sonra fiskal satış
                let tapx_rrn = '';
                if (prep.data?.tapx_required) {
                    const res = await tapxCardCharge(orderId, prep.data.tapx_amount || amt,
                        (m) => setPosPay({ open: true, closable: false, error: false, message: m }));
                    tapx_rrn = res.rrn;
                    setPosPay({ open: true, closable: false, error: false, message: 'Kart qəbul edildi. Fiskal satış göndərilir...' });
                    prep = await api.post(`/orders/${orderId}/pay-prepare`, { ...params, tapx_rrn });
                }

                if (prep.data?.relay) {
                    if (willCharge && !tapx_rrn) setPosPay({ open: true, closable: false, error: false, message: `${amtLabel} — müştəri kartı yaxınlaşdırsın, cavab gözlənilir...` });
                    const fwd = await fiscalForward(prep.data.relay); // cihaz kartı çəkir (SmartOne/integrasiya); uğursuzsa xəta
                    if (willCharge) setPosPay({ open: true, closable: false, error: false, message: 'Ödəniş qəbul edildi. Hesab bağlanır...' });
                    const r = await api.post(`/orders/${orderId}/pay`, { ...params, fiscal_raw: fwd.raw, fiscal_status: fwd.status, tapx_rrn });
                    setPosPay({ open: false, message: '', closable: false, error: false });
                    return r;
                }
                setPosPay({ open: false, message: '', closable: false, error: false });
                return await api.post(`/orders/${orderId}/pay`, { ...params, tapx_rrn });
            }
            return await api.post(`/orders/${orderId}/pay`, params);
        } catch (err) {
            // Sayım açıqdır və istifadəçi admindir → təsdiq soruş, "bəli"də force ilə təkrar.
            // (Bu 409 fiskal əməliyyatdan ƏVVƏL qayıdır, ona görə təkrar təhlükəsizdir.)
            if (!forced && err.response?.data?.code === 'SAYIM_CONFIRM') {
                setPosPay({ open: false, message: '', closable: false, error: false });
                if (await confirm(err.response?.data?.msg || 'Sayım bağlanmayıb. Yenə də təsdiq edək?')) {
                    return doPayRequest(payParams, true);
                }
                throw new Error('__cancelled__');
            }
            // Relay popup-u açıqdırsa xətanı orada göstər (imtina/pulsuz), caller toast etməsin
            if (useRelay && willCharge) {
                setPosPay({ open: true, closable: true, error: true, message: err.response?.data?.msg || err.message || 'Ödəniş imtina / baş tutmadı. Hesab açıq qalır.' });
                throw new Error('__handled__');
            }
            throw err;
        }
    };

    const handlePay = async (manual = false) => {
        if (!canPay) return;
        if (!displayItems.length) {
            toast.error('Sifarişdə məhsul yoxdur.');
            return;
        }

        if (method === 'hesab' && mixedTotal < grandTotal - 0.01) {
            toast.error('Nağd + kart məbləği ümumi cəmdən az ola bilməz.');
            return;
        }

        setPaying(true);
        try {
            const res = await doPayRequest({
                method,
                amount: method === 'hesab' ? mixedTotal : paidAmount,
                cash_amount: method === 'hesab' ? toMoney(cashAmount) : (method === 'nagd' ? paidAmount : 0),
                card_amount: method === 'hesab' ? toMoney(cardAmount) : (method === 'kart' ? paidAmount : 0),
                service_exempt: serviceExempt   // ödəniş zamanı servis söndürülübsə hesabata da yazılsın
            }, false, manual);
            const smartone = res.data?.smartone?.data;
            const caspos = res.data?.caspos?.data;
            if ((smartone?.status === 'success' && smartone?.code === 0) || Number(caspos?.code ?? -1) === 0) {
                toast.success('Ödəniş uğurlu, kassaya göndərildi', { duration: 5000 });
            } else {
                toast.success('Ödəniş qəbul edildi.');
            }
            navigate(`/waiter/receipt/${orderId}`);
        } catch (err) {
            if (err.message !== '__cancelled__' && err.message !== '__handled__') {
                toast.error(err.response?.data?.msg || err.message || 'Ödəniş uğursuz oldu.');
            }
        } finally {
            setPaying(false);
        }
    };

    // Ofisiant (kassirsiz) — hesabı POS terminala göndər, kart çəkilsə avtomatik bağlanır
    const handleWaiterPos = async () => {
        if (!displayItems.length) {
            toast.error('Sifarişdə məhsul yoxdur.');
            return;
        }
        if (!canFiscalRelay()) {
            toast.error('POS ödənişi yalnız Fiscal POS proqramında mümkündür.');
            return;
        }
        setPaying(true);
        try {
            await doPayRequest({
                method: 'kart',
                amount: grandTotal,
                cash_amount: 0,
                card_amount: grandTotal,
                service_exempt: serviceExempt
            });
            toast.success('Ödəniş uğurlu — hesab bağlandı.', { duration: 5000 });
            navigate(`/waiter/receipt/${orderId}`);
        } catch (err) {
            if (err.message !== '__cancelled__' && err.message !== '__handled__') {
                toast.error(err.response?.data?.msg || err.message || 'Ödəniş uğursuz oldu.');
            }
        } finally {
            setPaying(false);
        }
    };

    // Ofisiant (icazəsi varsa) — hesabı Food delivery ilə bağla (nağd/kart götürülmür)
    const handleWaiterDelivery = async () => {
        if (!displayItems.length) {
            toast.error('Sifarişdə məhsul yoxdur.');
            return;
        }
        if (!(await confirm(`Hesab Food delivery ilə bağlansın? (${formatMoney(grandTotal)})`))) return;
        setPaying(true);
        try {
            await doPayRequest({
                method: 'delivery',
                amount: grandTotal,
                cash_amount: 0,
                card_amount: 0,
                service_exempt: serviceExempt
            });
            toast.success('Hesab Food delivery ilə bağlandı.');
            navigate(`/waiter/receipt/${orderId}`);
        } catch (err) {
            if (err.message !== '__cancelled__' && err.message !== '__handled__') {
                toast.error(err.response?.data?.msg || err.message || 'Ödəniş uğursuz oldu.');
            }
        } finally {
            setPaying(false);
        }
    };

    const handleSaveCharges = async () => {
        if (!canEditCharges) return;

        setSavingCharges(true);
        try {
            await api.put(`/orders/${orderId}/charges`, {
                discount_amount: toMoney(discountAmount),
                deposit_amount: canUseDeposit ? toMoney(order?.table_deposit_amount || order?.deposit_amount || depositAmount) : 0,
                service_exempt: serviceExempt,
                deposit_waived: depositWaived
            });
            await loadOrder();
            toast.success('Endirim yeniləndi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Məlumat yadda saxlanmadı.');
        } finally {
            setSavingCharges(false);
        }
    };

    const handlePrint = async () => {
        if (!displayItems.length) {
            toast.error('Sifarişdə məhsul yoxdur.');
            return;
        }

        if (isFree) {
            setShowUpgradeModal(true);
            return;
        }

        setPrinting(true);

        try {
            const receiptPrinter = await getReceiptPrinterName();

            // ── Yarışma çeki — müştəri çekindən ƏVVƏL, ATOMIK + ARTIMLI ──
            // Backend hədəfi (cəm/threshold) hesablayır, artıq çıxanların FƏRQİNİ atomik
            // verir: 180→1 çıxıb, 200 olanda 1 əlavə. Reprint (eyni cəm) → 0. Eyni anda
            // 2 POS → ümumi hədəfi keçmir. Müştəri çeki hər halda tək çıxır.
            // KÖK HƏLL: sayğac yalnız TƏSDİQLƏNMİŞ çapdan sonra artır (commit). Çap
            // alınmasa commit olunmur → qalıq düzgün qalır, növbəti çapda təkrar çıxır.
            try {
                const pend = await api.post(`/orders/${orderId}/gift-tickets/pending`);
                const pending = Number(pend.data?.pending || 0);
                for (let i = 0; i < pending; i++) {
                    // Printer ardıcıl çapı həzm etsin deyə hər biletdən əvvəl qısa ara
                    if (i > 0) await new Promise(r => setTimeout(r, 800));
                    let ok = false;
                    for (let attempt = 0; attempt < 3 && !ok; attempt++) {
                        try {
                            await printGift(receiptPrinter, {
                                receiptLogoUrl: getReceiptLogoUrl(user),
                                orderId: order.id,
                                grandTotal
                            });
                            ok = true;
                        } catch (oneErr) {
                            console.error(`[gift-ticket print ${i + 1}/cəhd ${attempt + 1}]`, oneErr);
                            await new Promise(r => setTimeout(r, 500)); // qısa gözləyib təkrar
                        }
                    }
                    if (!ok) break; // çap alınmadı → sayğac artmır, sonra təkrar çıxacaq
                    // TƏSDİQ olunca atomik +1 (+ log). Başqa cihaz götürübsə/hədəf dolubsa dayan.
                    try {
                        const c = await api.post(`/orders/${orderId}/gift-tickets/commit`);
                        if (!c.data?.committed) break;
                    } catch (commitErr) {
                        console.error('[gift-ticket commit]', commitErr);
                        break;
                    }
                }
            } catch (giftErr) {
                console.error('[gift-ticket]', giftErr);
            }

            await printBill(receiptPrinter, {
                title: getReceiptTitle(user),
                receiptLogoUrl: getReceiptLogoUrl(user),
                phone: order.tenant_phone || '',
                orderId: order.id,
                dailyNumber: order.daily_number || null,   // fast food gündəlik sıra nömrəsi
                tableName: order.table_name,
                waiterName: order.waiter_name,
                waiterTitle: order.waiter_title || '',
                subtotal,
                servicePercent,
                serviceAmount: serviceAmountValue,
                discountAmount: discountAmountValue,
                depositAmount: depositTargetValue,
                appliedDeposit: appliedDepositValue,
                grandTotal,
                total: grandTotal,
                note: order.note || null,
                receiptText: buildReceiptText(order, user),
                items: (order.items || []).filter((it) => !Number(it.is_banquet_component)).map((item) => ({
                    name: item.item_name,
                    qty: Number(item.qty),
                    // Saatlıq: miqdar sütununda kompakt vaxt (2s30d), notда tam izah, tarif ₼/saat, cəmi = məbləğ
                    qtyLabel: Number(item.is_hourly) ? fmtElapsedShort(item.hourly_elapsed_sec) : formatQty(item.qty, item.sale_type),
                    unitPrice: Number(item.is_hourly) ? Number(item.hourly_rate || 0) : Number(item.unit_price),
                    saleType: item.sale_type || 'piece',
                    note: Number(item.is_hourly)
                        ? [`${fmtElapsed(item.hourly_elapsed_sec)} × ${parseFloat(item.hourly_rate || 0).toFixed(2)} ₼/saat`, item.note].filter(Boolean).join(' — ')
                        : (item.note || null),
                    total: parseFloat(item.unit_price) * Number(item.qty)
                }))
            });

            await api.post(`/orders/${orderId}/mark-bill-printed`);
            await loadOrder();
            toast.success('Müştəri çeki printerə göndərildi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || err.message || 'Çap baş tutmadı.');
        } finally {
            setPrinting(false);
        }
    };

    const methods = [
        { key: 'nagd', label: 'Nağd', icon: '💵' },
        { key: 'kart', label: 'Kart', icon: '💳' },
        { key: 'hesab', label: 'Hesab böl', icon: '🧾' },
        ...(foodDelivery ? [{ key: 'delivery', label: 'Food delivery', icon: '🛵' }] : [])
    ];
    // «Yalnız Food delivery» otağının masası → yeganə ödəniş növü delivery (nağd/kart/bölünmüş yoxdur)
    const payMethods = deliveryOnly ? [{ key: 'delivery', label: 'Food delivery', icon: '🛵' }] : methods;

    if (loading) {
        return <div className="flex h-screen items-center justify-center text-gray-400">Yüklənir...</div>;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <UpgradeModal
                open={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title="Bu funksiya Pulsuz paketdə bağlıdır"
                message="Müştəri çeki çapı üçün Sadə və ya Pro plana keçin."
                plans={['basic', 'pro']}
                currentPlan={user?.tenant_plan}
            />

            {/* POS kart ödənişi prosesi — bloklayan popup (yalnız imtina/xəta olanda bağlanır) */}
            {posPay.open && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
                     onClick={(e) => { if (posPay.closable && e.target === e.currentTarget) setPosPay({ open: false, message: '', closable: false, error: false }); }}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl text-center">
                        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-4 ${posPay.error ? 'bg-red-100' : 'bg-blue-100'}`}>
                            {posPay.error ? '❌' : <span className="inline-block h-8 w-8 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />}
                        </div>
                        <h3 className="text-base font-bold text-gray-800 mb-1">💳 POS ödənişi</h3>
                        <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{posPay.message}</p>
                        {posPay.closable ? (
                            <button onClick={() => setPosPay({ open: false, message: '', closable: false, error: false })}
                                className="mt-5 w-full py-2.5 rounded-xl text-sm font-bold text-white bg-gray-600 hover:bg-gray-700">Bağla</button>
                        ) : (
                            <p className="mt-4 text-[11px] text-gray-400">Zəhmət olmasa gözləyin — prosesi dayandırmayın.</p>
                        )}
                    </div>
                </div>
            )}

            <nav className="sticky top-0 z-50 flex h-14 items-center gap-3 border-b border-gray-200 bg-white px-4">
                <button
                    onClick={() => navigate(isKassir ? '/waiter/tables' : `/waiter/order/${orderId}`)}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-500 transition-all hover:border-primary-400 hover:text-primary-500"
                >
                    ← Geri
                </button>
                <span className="font-bold text-gray-800">Hesab — {order?.table_name}</span>
            </nav>

            <div className="mx-auto max-w-4xl px-4 py-6">
                <div className={`grid gap-6 ${canPay ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                            <h3 className="font-bold text-gray-800">🧾 Hesab</h3>
                            <span className="text-xs text-gray-400">
                                #{orderId} · {order?.opened_at && fmtTime(order.opened_at)}
                            </span>
                        </div>

                        <div className="flex justify-between border-b border-dashed border-gray-200 px-5 py-3 text-xs text-gray-500">
                            <span>📍 {order?.table_name}</span>
                            <span>👤 {order?.waiter_name}</span>
                        </div>

                        <div className="px-5 py-3">
                            <div className="mb-1 flex gap-2 border-b border-gray-100 pb-2 text-xs font-semibold uppercase text-gray-400">
                                <span className="flex-1">Məhsul</span>
                                <span className="w-16 text-center">Miqdar</span>
                                <span className="w-16 text-right">Vahid</span>
                                <span className="w-16 text-right">Cəmi</span>
                            </div>

                            {displayItems.map((item) => (
                                <div key={item.id} className="flex gap-2 border-b border-gray-50 py-2 last:border-0">
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-800">
                                            {item._isSetGroup ? `🎁 ${item.item_name}` : item.item_name}
                                        </div>
                                        {!item._isSetGroup && item.note && <div className="text-xs text-amber-500">📝 {item.note}</div>}
                                    </div>
                                    <span className="w-16 text-center font-mono text-xs text-gray-400">
                                        {item._isSetGroup ? `${item.qty}ed`
                                            : Number(item.is_hourly) ? fmtElapsed(item.hourly_elapsed_sec)
                                            : formatQty(item.qty, item.sale_type)}
                                    </span>
                                    <span className="w-16 text-right font-mono text-xs text-gray-400">
                                        {Number(item.is_hourly)
                                            ? `${parseFloat(item.hourly_rate || 0).toFixed(0)}₼/s`
                                            : parseFloat(item.unit_price).toFixed(2)}
                                    </span>
                                    <span className="w-16 text-right font-mono text-sm font-bold text-gray-700">
                                        {(parseFloat(item.unit_price) * Number(item.qty)).toFixed(2)}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-dashed border-gray-200 bg-gray-50 px-5 py-4">
                            <div className="mb-1 flex justify-between text-sm text-gray-500">
                                <span>Cəm</span>
                                <span className="font-mono">{formatMoney(subtotal)}</span>
                            </div>
                            <div className="mb-1 flex justify-between text-sm text-gray-500">
                                <span>{isTakeaway ? `Takeaway üçün əlavə ${trimQty(servicePercent, 2)}%` : `${trimQty(servicePercent, 2)}% servis haqqı`}</span>
                                <span className="font-mono">{formatMoney(serviceAmountValue)}</span>
                            </div>
                            <div className="mb-1 flex justify-between text-sm text-gray-500">
                                <span>Depozit</span>
                                <span className="font-mono">{formatMoney(appliedDepositValue)}</span>
                            </div>
                            <div className="mb-3 flex justify-between text-sm text-gray-500">
                                <span>Endirim</span>
                                <span className="font-mono">{formatMoney(discountAmountValue)}</span>
                            </div>
                            <div className="flex items-center justify-between border-t border-gray-200 pt-2">
                                <span className="text-base font-bold text-gray-800">Ümumi cəm</span>
                                <span className="font-mono text-2xl font-bold text-gray-800">{formatMoney(grandTotal)}</span>
                            </div>
                        </div>

                        {order?.note && <div className="border-t border-gray-100 px-5 py-3 text-xs text-gray-500">📝 {order.note}</div>}

                        <div className="border-t border-gray-100 px-5 py-4">
                            <button
                                onClick={handlePrint}
                                disabled={printing}
                                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-primary-200 py-2.5 text-sm font-bold text-primary-600 transition-all hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {printing ? '⏳ Çap olunur...' : '🖨️ Müştəri (çap)'}
                            </button>
                        </div>
                    </div>

                    {isWaiterOnly && canFiscalRelay() && posTerminal && !deliveryOnly && (
                        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                            <div className="border-b border-gray-100 px-5 py-4">
                                <h3 className="font-bold text-gray-800">💳 POS ilə ödəniş</h3>
                                <p className="mt-0.5 text-xs text-gray-500">Hesabı POS terminala göndər — kart çəkilsə avtomatik bağlanır</p>
                            </div>
                            <div className="p-5">
                                <button
                                    onClick={handleWaiterPos}
                                    disabled={paying || !order?.items?.length}
                                    className="w-full rounded-2xl py-4 text-base font-black text-white shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-60"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                >
                                    {paying ? 'Emal olunur...' : `💳 POS-a göndər · ${formatMoney(grandTotal)}`}
                                </button>
                            </div>
                        </div>
                    )}

                    {isWaiterOnly && foodDelivery && (
                        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                            <div className="border-b border-gray-100 px-5 py-4">
                                <h3 className="font-bold text-gray-800">🛵 Food delivery</h3>
                                <p className="mt-0.5 text-xs text-gray-500">Sifariş çatdırılma platformasındandır. Ödəniş kassaya ayrıca yazılır.</p>
                            </div>
                            <div className="p-5">
                                <button
                                    onClick={handleWaiterDelivery}
                                    disabled={paying || !order?.items?.length}
                                    className="w-full rounded-2xl border-2 border-orange-200 bg-orange-50 py-4 text-base font-black text-orange-700 transition-all hover:-translate-y-0.5 hover:bg-orange-100 disabled:opacity-60"
                                >
                                    {paying ? 'Emal olunur...' : `🛵 Food delivery ilə bağla · ${formatMoney(grandTotal)}`}
                                </button>
                            </div>
                        </div>
                    )}

                    {canPay && (
                        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                            <div className="border-b border-gray-100 px-5 py-4">
                                <h3 className="font-bold text-gray-800">💰 Ödəniş qəbul et</h3>
                            </div>

                            <div className="flex flex-col gap-5 p-5">
                                {canEditCharges && (
                                    <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-4">
                                        <div className="mb-3 flex items-center justify-between">
                                            <h4 className="text-sm font-bold text-gray-800">Admin endirimi</h4>
                                            <span className="text-[11px] font-semibold text-amber-700">Servis: %{trimQty(servicePercent, 2)}</span>
                                        </div>
                                        <label className="mb-3 flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={serviceExempt}
                                                onChange={(e) => handleServiceExemptChange(e.target.checked)}
                                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                            Servis haqqını bu masa üçün 0% et
                                        </label>

                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            <div>
                                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Endirim (₼)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={discountAmount}
                                                    onChange={(e) => setDiscountAmount(e.target.value)}
                                                    className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none"
                                                />
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {[10, 20, 50, 100].map((percent) => (
                                                        <button
                                                            key={percent}
                                                            type="button"
                                                            onClick={() => applyDiscountPreset(percent)}
                                                            className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[11px] font-bold text-amber-700 transition-all hover:bg-amber-100"
                                                        >
                                                            {percent}%
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Depozit hədəfi (₼)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={depositAmount}
                                                    readOnly
                                                    disabled
                                                    className="w-full rounded-xl border-2 border-gray-100 bg-gray-100 px-3 py-2.5 text-sm text-gray-400 focus:outline-none"
                                                />
                                                <div className="mt-1 text-[11px] font-semibold text-gray-500">Depozit yalnız Masalar bölməsindən idarə olunur.</div>
                                            </div>
                                        </div>

                                        {canUseDeposit && toMoney(depositAmount) > 0 && (
                                            <label className="mt-3 flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700">
                                                <input
                                                    type="checkbox"
                                                    checked={depositWaived}
                                                    onChange={(e) => setDepositWaived(e.target.checked)}
                                                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                />
                                                Depoziti bu dəfəlik ləğv et (0 ₼)
                                            </label>
                                        )}

                                        <button
                                            onClick={handleSaveCharges}
                                            disabled={savingCharges}
                                            className="mt-4 w-full rounded-xl border border-amber-200 bg-white py-2.5 text-sm font-bold text-amber-700 transition-all hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {savingCharges ? 'Yadda saxlanır...' : 'Endirimi yadda saxla'}
                                        </button>
                                    </div>
                                )}

                                <div>
                                    <label className="mb-3 block text-xs font-semibold uppercase tracking-wider text-gray-500">Ödəniş növü</label>
                                    <div className={`grid gap-2 ${payMethods.length > 3 ? 'grid-cols-2 sm:grid-cols-4' : payMethods.length === 1 ? 'grid-cols-1' : 'grid-cols-3'}`}>
                                        {payMethods.map((currentMethod) => (
                                            <button
                                                key={currentMethod.key}
                                                onClick={() => handleMethodChange(currentMethod.key)}
                                                className={`rounded-xl border-2 py-3 text-center transition-all ${
                                                    method === currentMethod.key
                                                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                                                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                                                }`}
                                            >
                                                <div className="mb-1 text-2xl">{currentMethod.icon}</div>
                                                <div className="text-xs font-bold">{currentMethod.label}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {method === 'hesab' ? (
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">Nağd (₼)</label>
                                            <input
                                                ref={cashInputRef}
                                                type="number"
                                                value={cashAmount}
                                                onChange={(e) => handleCashAmountChange(e.target.value)}
                                                onFocus={() => activateSplitField('cash')}
                                                step="0.01"
                                                min="0"
                                                readOnly={splitDriver === 'card'}
                                                className={`w-full rounded-xl border-2 px-4 py-4 text-center font-mono text-xl font-bold focus:outline-none ${
                                                    splitDriver === 'card'
                                                        ? 'border-gray-100 bg-gray-50 text-gray-400'
                                                        : 'border-gray-200 focus:border-primary-500'
                                                }`}
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">Kart (₼)</label>
                                            <input
                                                ref={cardInputRef}
                                                type="number"
                                                value={cardAmount}
                                                onChange={(e) => handleCardAmountChange(e.target.value)}
                                                onFocus={() => activateSplitField('card')}
                                                readOnly={splitDriver === 'cash'}
                                                step="0.01"
                                                min="0"
                                                className={`w-full rounded-xl border-2 px-4 py-4 text-center font-mono text-xl font-bold focus:outline-none ${
                                                    splitDriver === 'cash'
                                                        ? 'border-gray-100 bg-gray-50 text-gray-400'
                                                        : 'border-gray-200 focus:border-primary-500'
                                                }`}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">Alınan məbləğ (₼)</label>
                                        <input
                                            type="number"
                                            value={(method === 'kart' || method === 'delivery') ? grandTotal.toFixed(2) : amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            readOnly={method !== 'nagd'}
                                            step="0.01"
                                            min="0"
                                            className={`w-full rounded-xl border-2 px-4 py-4 text-center font-mono text-2xl font-bold transition-all focus:outline-none ${
                                                method !== 'nagd' ? 'border-gray-100 bg-gray-50 text-gray-400' : 'border-gray-200 focus:border-primary-500'
                                            }`}
                                        />
                                    </div>
                                )}

                                {method === 'nagd' && change > 0.005 && (
                                    <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
                                        <div className="mb-1 text-xs font-semibold text-green-600">Qaytarılacaq</div>
                                        <div className="font-mono text-3xl font-bold text-green-600">{formatMoney(change)}</div>
                                    </div>
                                )}

                                {method === 'hesab' && (
                                    <div className={`rounded-xl border p-4 text-center ${mixedTotal + 0.0001 >= grandTotal ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                                        <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">Bölünmüş ödəniş cəmi</div>
                                        <div className="font-mono text-2xl font-bold text-gray-800">{formatMoney(mixedTotal)}</div>
                                    </div>
                                )}

                                <div className="rounded-xl bg-gray-50 p-4">
                                    <div className="mb-1 flex items-center justify-between text-sm text-gray-500">
                                        <span>Ödəniləcək</span>
                                        <span className="font-mono">{formatMoney(grandTotal)}</span>
                                    </div>
                                    {method === 'hesab' && (
                                        <div className="flex items-center justify-between text-sm text-gray-500">
                                            <span>Nağd + kart</span>
                                            <span className="font-mono">{formatMoney(mixedTotal)}</span>
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={() => handlePay(false)}
                                    disabled={paying || !order?.items?.length}
                                    className="w-full rounded-xl py-4 text-base font-bold text-white transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)', boxShadow: '0 4px 20px rgba(0,144,187,0.25)' }}
                                >
                                    {paying ? 'Emal olunur...' : `✓ Ödənişi təsdiqlə · ${formatMoney(grandTotal)}`}
                                </button>

                                {/* Admin: fiskal kassaya getmədən MANUAL bağla (kassir/ofisiant görmür) */}
                                {isAdminUser && (
                                    <button
                                        onClick={async () => { if (await confirm('Fiskal kassaya göndərmədən manual bağlansın?')) handlePay(true); }}
                                        disabled={paying || !order?.items?.length}
                                        className="w-full rounded-xl border border-gray-300 py-2.5 text-sm font-bold text-gray-600 transition-all hover:bg-gray-50 disabled:opacity-40"
                                    >
                                        ⚡ Manual bağla (kassasız)
                                    </button>
                                )}

                                {!isKassir && !isWaiterOnly && (
                                    <button
                                        onClick={() => navigate(`/waiter/order/${orderId}`)}
                                        className="text-sm text-gray-400 transition-all hover:text-primary-500"
                                    >
                                        ← Sifarişə qayıt
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WaiterBill;
